import React, { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import Seo from "../seo/Seo";
import { PageHero } from "../components/ui-kit";
import { useToast } from "../hooks/use-toast";
import { BUSINESS } from "../data/site";
import { IMAGES } from "../mock/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { name: "", email: "", phone: "", preferredContact: "", city: "", county: "", ownsLand: "", timeline: "", homeSize: "", bedrooms: "", bathrooms: "", homeStyle: "", message: "" };

const breadcrumbs = [{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }];
const inputCls = "w-full bg-[#0b0b0b] border border-[#2a2a2a] focus:border-[#c9a24b] outline-none px-4 py-3 text-[#f3ede0] transition-colors";
const labelCls = "block text-xs uppercase tracking-wider text-[#a39d90] mb-2";

const Field = ({ label, children }) => (<div><label className={labelCls}>{label}</label>{children}</div>);

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const up = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please complete the required fields", description: "Name, email, and project description are required." });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        service: form.homeStyle || "Custom Home",
      });
      setForm(initial);
      toast({ title: "Thank you! 🏡", description: "Your custom home inquiry has been received. A Build Highland Homes representative will reach out shortly." });
    } catch {
      toast({ title: "Something went wrong", description: "We couldn't send your inquiry. Please try again or call us directly." });
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <Seo
        title="Contact Build Highland Homes | Start Your Custom Home Project"
        description="Start your custom home project with Build Highland Homes. Tell us about your lot and your vision — serving Brevard, Orange, Osceola, Seminole & Volusia counties in Central Florida."
        path="/contact"
        keywords="contact custom home builder, start custom home project, custom home consultation central florida"
        breadcrumbs={breadcrumbs}
      />
      <PageHero overline="Get In Touch" title="Start Your Custom Home Project" subtitle="Tell us about your property and your vision. We'll help you take the first step toward a custom home built on your lot in Central Florida." image={IMAGES.hero} />

      <section className="py-16 md:py-24 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-3 gap-12">
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center border border-[#c9a24b]/40 text-[#c9a24b] shrink-0"><MapPin size={20} /></div>
              <div><p className="text-[#a39d90] text-xs uppercase tracking-wider">Service Area</p><p className="text-[#f3ede0]">{BUSINESS.areaServedText}</p></div>
            </div>
            <a href={`mailto:${BUSINESS.email}`} className="flex items-start gap-4 group">
              <div className="w-12 h-12 flex items-center justify-center border border-[#c9a24b]/40 text-[#c9a24b] shrink-0 group-hover:bg-[#c9a24b] group-hover:text-[#0b0b0b] transition-colors"><Mail size={20} /></div>
              <div><p className="text-[#a39d90] text-xs uppercase tracking-wider">Email</p><p className="text-[#f3ede0] group-hover:text-[#e3c77a] transition-colors break-all">{BUSINESS.email}</p></div>
            </a>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center border border-[#c9a24b]/40 text-[#c9a24b] shrink-0"><Phone size={20} /></div>
              <div>
                <p className="text-[#a39d90] text-xs uppercase tracking-wider">Phone</p>
                {BUSINESS.phones.map((p) => <a key={p} href={`tel:${p.replace(/[^0-9]/g, "")}`} className="block text-[#f3ede0] hover:text-[#e3c77a] transition-colors">{p}</a>)}
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-2 bg-[#141414] border border-[#c9a24b]/20 p-7 md:p-9">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full Name *"><input value={form.name} onChange={up("name")} className={inputCls} placeholder="John Doe" /></Field>
              <Field label="Email *"><input type="email" value={form.email} onChange={up("email")} className={inputCls} placeholder="you@email.com" /></Field>
              <Field label="Phone"><input value={form.phone} onChange={up("phone")} className={inputCls} placeholder="(000) 000-0000" /></Field>
              <Field label="Preferred Contact"><select value={form.preferredContact} onChange={up("preferredContact")} className={inputCls}><option value="">Select…</option><option>Phone</option><option>Email</option><option>Text</option></select></Field>
              <Field label="City"><input value={form.city} onChange={up("city")} className={inputCls} placeholder="e.g., Melbourne" /></Field>
              <Field label="County"><select value={form.county} onChange={up("county")} className={inputCls}><option value="">Select…</option>{BUSINESS.counties.map((c) => <option key={c}>{c}</option>)}</select></Field>
              <Field label="Do you already own land?"><select value={form.ownsLand} onChange={up("ownsLand")} className={inputCls}><option value="">Select…</option><option>Yes, I own the lot</option><option>Under contract</option><option>Not yet</option></select></Field>
              <Field label="Estimated Timeline"><select value={form.timeline} onChange={up("timeline")} className={inputCls}><option value="">Select…</option><option>ASAP</option><option>3–6 months</option><option>6–12 months</option><option>12+ months</option></select></Field>
              <Field label="Approx. Home Size (sq ft)"><input value={form.homeSize} onChange={up("homeSize")} className={inputCls} placeholder="e.g., 2,500" /></Field>
              <Field label="Home Style"><select value={form.homeStyle} onChange={up("homeStyle")} className={inputCls}><option value="">Select…</option><option>One-Story Custom Home</option><option>Two-Story Custom Home</option><option>Modern Barndominium</option><option>Home Addition</option><option>Not sure yet</option></select></Field>
              <Field label="Bedrooms"><input value={form.bedrooms} onChange={up("bedrooms")} className={inputCls} placeholder="e.g., 4" /></Field>
              <Field label="Bathrooms"><input value={form.bathrooms} onChange={up("bathrooms")} className={inputCls} placeholder="e.g., 3" /></Field>
            </div>
            <div className="mt-5">
              <Field label="Project Description *"><textarea rows={4} value={form.message} onChange={up("message")} className={`${inputCls} resize-none`} placeholder="Tell us about your vision, your property, and anything else that would help…" /></Field>
            </div>
            <button type="submit" disabled={submitting} className="mt-7 w-full flex items-center justify-center gap-2 bg-[#c9a24b] hover:bg-[#e3c77a] disabled:opacity-60 text-[#0b0b0b] font-medium tracking-wide px-6 py-4 transition-all duration-300">
              {submitting ? "Sending…" : <>Start Your Custom Home Project <Send size={16} /></>}
            </button>
          </form>
        </div>
      </section>
    </>
  );
};

export default Contact;
